import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { toast } from 'react-toastify'

import { AppDispatch, RootState } from 'store'
import { getErrorMessage } from 'utils/helpers'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { updateContactTags } from 'services/api/creators'
import { resetCreatorsState } from 'slices/creators/reducer'
import TagsSelector from './TagsSelector'
import './HandleCreatorTagsModal.scss'

export interface HandleCreatorTagsModalState {
    socialAccountId: string
    tags: string[]
    creatorName?: string
}

const HandleCreatorTagsModal: React.FC<{
    isOpen: boolean
    onClose: () => void
    socialAccountId?: string
    initialTags?: string[]
    creatorName?: string
}> = ({ isOpen, onClose, socialAccountId, initialTags = [], creatorName }) => {
    const [processing, setProcessing] = useState(false)
    const [tags, setTags] = useState<string[]>([])
    const dispatch = useDispatch<AppDispatch>()
    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId)
    const filters = useSelector((state: RootState) => state.creators.filters)

    const handleSubmit = async () => {
        if (!selectedHubId || !socialAccountId) return
        try {
            setProcessing(true)
            await updateContactTags(selectedHubId, socialAccountId, tags)
            toast.success('Creator tags updated successfully.')
            dispatch(resetCreatorsState())
            dispatch(fetchContactsThunk({ force: true, filters: { ...filters, page: 1 } }))
            onClose()
        } catch (error: unknown) {
            console.error(error)
            toast.error(
                getErrorMessage(error) || 'Failed to update creator tags. Please try again.'
            )
        } finally {
            setProcessing(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            setTags(initialTags ?? [])
        }
    }, [isOpen, initialTags])

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            className="handle-creator-tags-modal"
        >
            <ModalHeader className='border-bottom' toggle={onClose}>Edit Creator Tags
                {creatorName && (
                    <p className="modal-subtitle text-dark fw-medium mb-2">{creatorName}</p>
                )}
            </ModalHeader>
            <ModalBody>

                <div className="handle-creator-tags-form">
                    <div className="mb-0">
                        <label htmlFor="creator-tags" className="form-label">
                            Tags
                        </label>
                        <TagsSelector value={tags} onChange={setTags} />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="theme-btn btn-primary-outlined" onClick={onClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn btn-primary"
                    onClick={handleSubmit}
                    disabled={processing || !socialAccountId}
                >
                    {processing ? 'Saving...' : 'Save Tags'}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default HandleCreatorTagsModal
